import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyFDInterestAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyFDInterestQueue = createAsyncThunk(
    'verifyFDInterest/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationFDInterest(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch FD interest verification queue')); }
    }
);

export const fetchVerifyFDInterestDetail = createAsyncThunk(
    'verifyFDInterest/fetchDetail',
    async (transactionNo, { rejectWithValue }) => {
        try { return await api.getFDInterestByTransNo(transactionNo); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch FD interest details')); }
    }
);

export const fetchFDIntDeductionData = createAsyncThunk(
    'verifyFDInterest/fetchDeductionData',
    async (_, { rejectWithValue }) => {
        try { return await api.getFDIntDeductionData(); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch FD interest deduction settings')); }
    }
);

export const submitApproveFDInterest = createAsyncThunk(
    'verifyFDInterest/approve',
    async (payload, { rejectWithValue }) => {
        try { return await api.approveFDInterest(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    deductionData: null,
    verifyLoading: false,
};

const verifyFDInterestSlice = createSlice({
    name: 'verifyFDInterest',
    initialState,
    reducers: {
        clearVerifyFDInterestDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyFDInterest: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyFDInterestQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyFDInterestQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyFDInterestQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchVerifyFDInterestDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
            .addCase(fetchVerifyFDInterestDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload?.Data || null; })
            .addCase(fetchVerifyFDInterestDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(fetchFDIntDeductionData.fulfilled, (s, a) => { s.deductionData = a.payload?.Data || null; });

        builder
            .addCase(submitApproveFDInterest.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitApproveFDInterest.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitApproveFDInterest.rejected, (s) => { s.verifyLoading = false; });
    },
});

export const { clearVerifyFDInterestDetail, resetVerifyFDInterest } = verifyFDInterestSlice.actions;

export const selectVFIQueue = (s) => s.verifyFDInterest.queue;
export const selectVFIQueueLoading = (s) => s.verifyFDInterest.queueLoading;
export const selectVFIQueueError = (s) => s.verifyFDInterest.queueError;
export const selectVFIDetail = (s) => s.verifyFDInterest.detail;
export const selectVFIDetailLoading = (s) => s.verifyFDInterest.detailLoading;
export const selectVFIDetailError = (s) => s.verifyFDInterest.detailError;
export const selectVFIDeductionData = (s) => s.verifyFDInterest.deductionData;
export const selectVFIVerifyLoading = (s) => s.verifyFDInterest.verifyLoading;

export default verifyFDInterestSlice.reducer;
